import React from 'react';
import { motion } from 'framer-motion';
import { 
  Atom,
  Shield, 
  Lock, 
  Eye, 
  Bell, 
  Server, 
  Cloud,
  Database,
  CheckCircle,
  ArrowRight,
  Users,
  Clock,
  AlertTriangle,
  FileCheck,
  Network,
  Cpu
} from 'lucide-react';

interface NeutronSecurityProps {
  setCurrentPage: (page: string) => void;
}

const NeutronSecurity: React.FC<NeutronSecurityProps> = ({ setCurrentPage }) => {
  const features = [
    {
      icon: Shield,
      title: "Advanced Protection",
      description: "Enterprise-grade security features to protect your organization from sophisticated threats.",
      items: [
        "Next-gen firewall protection",
        "Advanced endpoint security",
        "Email security & phishing defense",
        "24/7 threat monitoring"
      ]
    },
    {
      icon: Lock,
      title: "Identity & Access Management",
      description: "Comprehensive access control and authentication solutions.",
      items: [
        "Multi-factor authentication",
        "Role-based access control",
        "Single sign-on (SSO)",
        "Privileged access management"
      ]
    },
    {
      icon: Eye,
      title: "Enhanced Threat Detection",
      description: "Advanced threat detection and response capabilities.",
      items: [
        "AI-powered threat detection",
        "Behavioral analytics",
        "Zero-day threat protection",
        "Advanced security analytics"
      ]
    },
    {
      icon: Network,
      title: "Network Security",
      description: "Comprehensive network protection and monitoring.",
      items: [
        "Network segmentation",
        "Traffic analysis",
        "DDoS protection",
        "Secure remote access"
      ]
    }
  ];

  const services = [
    {
      icon: Server,
      title: "Infrastructure Security",
      description: "Enterprise infrastructure protection with advanced security measures.",
      items: [
        "Infrastructure hardening",
        "Vulnerability management",
        "Configuration security",
        "Patch management"
      ]
    },
    {
      icon: Cloud,
      title: "Cloud Security",
      description: "Advanced cloud security for multi-cloud environments.",
      items: [
        "Cloud workload protection",
        "Container security",
        "Cloud access security",
        "Cloud compliance"
      ]
    },
    {
      icon: Database,
      title: "Data Protection",
      description: "Enterprise data security and privacy solutions.",
      items: [
        "Data encryption",
        "DLP solutions",
        "Database security",
        "Data governance"
      ]
    }
  ];

  const benefits = [
    {
      icon: Shield,
      title: "Enterprise Protection",
      description: "Comprehensive security for growing organizations"
    },
    {
      icon: Users,
      title: "Dedicated Support",
      description: "24/7 access to security experts and support"
    },
    {
      icon: Clock,
      title: "Rapid Response",
      description: "Swift incident response and remediation"
    },
    {
      icon: AlertTriangle,
      title: "Proactive Security",
      description: "Advanced threat prevention and detection"
    }
  ];

  const capabilities = [
    {
      icon: Cpu,
      title: "Advanced Analytics",
      items: [
        "Security analytics",
        "Threat intelligence",
        "Risk analytics",
        "Behavioral analysis"
      ]
    },
    {
      icon: FileCheck,
      title: "Compliance Management",
      items: [
        "Compliance monitoring",
        "Policy enforcement",
        "Audit management",
        "Regulatory reporting"
      ]
    },
    {
      icon: Eye,
      title: "Security Operations",
      items: [
        "24/7 monitoring",
        "Incident response",
        "Threat hunting",
        "Security automation"
      ]
    }
  ];

  return (
    <div className="min-h-screen hero-gradient pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block mb-6"
          >
            <Atom className="h-16 w-16 text-purple-400 animate-glow" />
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl font-bold mb-6"
          >
            <span className="text-gradient">Neutron Cybersecurity Solution</span>
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-muted max-w-3xl mx-auto"
          >
            Advanced enterprise security solution providing comprehensive protection for growing organizations
          </motion.p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + index * 0.1 }}
              className="glass-panel rounded-xl p-8 hover-card"
            >
              <div className="flex items-center mb-6">
                <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg">
                  <feature.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-2xl font-semibold ml-4">{feature.title}</h3>
              </div>
              
              <p className="text-muted mb-6">{feature.description}</p>
              
              <ul className="space-y-3">
                {feature.items.map((item, itemIndex) => (
                  <li key={itemIndex} className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-purple-500 mr-3 flex-shrink-0" />
                    <span className="text-muted">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Services Section */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Enterprise Services</h2>
            <p className="text-lg text-muted">
              Advanced security services for comprehensive protection
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + index * 0.1 }}
                className="glass-panel rounded-xl p-8 hover-card"
              >
                <div className="flex items-center mb-6">
                  <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg">
                    <service.icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-2xl font-semibold ml-4">{service.title}</h3>
                </div>
                
                <p className="text-muted mb-6">{service.description}</p>

                <ul className="space-y-3">
                  {service.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-purple-500 mr-3 flex-shrink-0" />
                      <span className="text-muted">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Benefits Section */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Key Benefits</h2>
            <p className="text-lg text-muted">
              Why choose Neutron Cybersecurity Solution
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + index * 0.1 }}
                className="glass-panel rounded-xl p-6 hover-card"
              >
                <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg mb-6">
                  <benefit.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-3">{benefit.title}</h3>
                <p className="text-muted">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Capabilities Section */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Advanced Capabilities</h2>
            <p className="text-lg text-muted">
              Enterprise-grade security capabilities
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {capabilities.map((capability, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 + index * 0.1 }}
                className="glass-panel rounded-xl p-8 hover-card"
              >
                <div className="flex items-center mb-6">
                  <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg">
                    <capability.icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-2xl font-semibold ml-4">{capability.title}</h3>
                </div>

                <ul className="space-y-3">
                  {capability.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-purple-500 mr-3 flex-shrink-0" />
                      <span className="text-muted">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="text-center"
        >
          <div className="glass-panel rounded-2xl p-8 md:p-12">
            <h2 className="text-3xl font-bold mb-6">Ready to Enhance Your Security?</h2>
            <p className="text-lg text-muted mb-8 max-w-2xl mx-auto">
              Contact our security experts to learn more about our Neutron Cybersecurity Solution and how it can protect your organization
            </p>
            <button 
              onClick={() => setCurrentPage('get-started')}
              className="btn-primary"
            >
              <span className="flex items-center justify-center">
                Request Consultation
                <ArrowRight className="ml-2 h-5 w-5" />
              </span>
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default NeutronSecurity;